import type { PlanLike } from './plan_lib'
import * as planLib from './plan_lib'

export type PlansLike = PlanLike[]

/**
 * 現在のプランのみをフィルタリング（実行中）
 * @param plans - プランの配列
 * @param date - 基準日（nullの場合は今日）
 * @param exceptId - 除外するプランのID（nullの場合は除外なし）
 * @returns フィルタリングされたプランの配列
 */
export const getCurrentPlans = <T extends PlanLike>(
  plans: T[],
  date: string | null,
  exceptId: number | null,
): T[] => {
  return plans.filter(
    (plan) => planLib.isCurrent(plan, date) && plan.id !== exceptId,
  )
}

/**
 * 過去のプランのみをフィルタリング（終了済み）
 * @param plans - プランの配列
 * @param date - 基準日（nullの場合は今日）
 * @param exceptId - 除外するプランのID（nullの場合は除外なし）
 * @returns フィルタリングされたプランの配列
 */
export const getPastPlans = <T extends PlanLike & { id: number }>(
  plans: T[],
  date: string | null,
  exceptId: number | null,
): T[] => {
  return plans.filter(
    (plan) => planLib.isPast(plan, date) && plan.id !== exceptId,
  )
}

/**
 * 未来のプランのみをフィルタリング（開始予定）
 * @param plans - プランの配列
 * @param date - 基準日（nullの場合は今日）
 * @param exceptId - 除外するプランのID（nullの場合は除外なし）
 * @returns フィルタリングされたプランの配列
 */
export const getFuturePlans = <T extends PlanLike & { id: number }>(
  plans: T[],
  date: string | null,
  exceptId: number | null,
): T[] => {
  return plans.filter(
    (plan) => planLib.isFuture(plan, date) && plan.id !== exceptId,
  )
}

/**
 * 指定月にアクティブなプランのみをフィルタリング
 * @param plans - プランの配列
 * @param month - YYYY-MM形式の月文字列
 * @param exceptId - 除外するプランのID（nullの場合は除外なし）
 * @returns フィルタリングされたプランの配列
 */
export const getMonthPlans = <T extends PlanLike & { id: number }>(
  plans: T[],
  month: string,
  exceptId: number | null,
): T[] => {
  return plans.filter(
    (plan) => planLib.isMonth(plan, month) && plan.id !== exceptId,
  )
}

/**
 * 指定月に請求対象のプランのみをフィルタリング（初月は無料のため除外）
 * @param plans - プランの配列
 * @param month - YYYY-MM形式の月文字列
 * @param exceptId - 除外するプランのID（nullの場合は除外なし）
 * @returns フィルタリングされたプランの配列
 */
export const getInvoicePlans = <T extends PlanLike & { id: number }>(
  plans: T[],
  month: string,
  exceptId: number | null,
): T[] => {
  return plans.filter(
    (plan) => planLib.isInvoice(plan, month) && plan.id !== exceptId,
  )
}
